import React, { useCallback, useMemo } from 'react';
import { Form, Formik } from 'formik';
import {
  OptionName,
  QuoteInformation,
  useCurrentOrganizationId,
  useOffers,
} from 'queries';
import * as Yup from 'yup';

import { LoginModal } from 'components';

import { GeneralSetup, GeneralSetupProps } from './GeneralSetup/GeneralSetup';
import { useCreateStudy } from './hooks/useCreateStudy';
import { hasProduct } from 'lib/billing';

interface Props extends GeneralSetupProps {
  source?: 'CLIENT' | 'TANDEMZ';
}

const initialValues: QuoteInformation = {
  source: undefined,
  studySubject: undefined,
  studyType: undefined,
  format: undefined,
  targetingOfferId: undefined,
  supportOfferId: undefined,
  optionsOfferIds: undefined,
  duration: 0,
  countryKeys: [],
  numberOfParticipantRequired: 0,
  haveQuote: false,
  quoteRef: '',
  billingMethod: undefined,
};

const useValidationSchema = () => {
  const { data: offers } = useOffers();
  return useMemo(
    () =>
      Yup.object().shape({
        numberOfParticipantRequired: Yup.number()
          .min(1)
          .test(
            'number-of-participant-limit',
            'form.errors.numberOfParticipantRequired.max',
            function (value) {
              if (this.parent.targetingOfferId && offers) {
                const offer = offers.find(
                  (o) => o.id === this.parent.targetingOfferId,
                );
                if (!value) {
                  return false;
                }
                if (
                  offer &&
                  hasProduct(
                    offer.products,
                    OptionName.LIMIT_PARTICIPANTS_TO_10,
                  )
                ) {
                  return value <= 10;
                }
                if (
                  offer &&
                  hasProduct(
                    offer.products,
                    OptionName.LIMIT_PARTICIPANTS_TO_300,
                  )
                ) {
                  return value <= 300;
                }
              }
              return true;
            },
          ),
      }),
    [offers],
  );
};

export const GeneralSetupForm = ({ source, ...rest }: Props) => {
  const validationSchema = useValidationSchema();
  const organizationId = useCurrentOrganizationId(true);
  const createDto = useCallback(
    (values: QuoteInformation) => {
      const optionsOfferIds = values.optionsOfferIds || [];
      return {
        organizationId,
        offerIds: [
          values.targetingOfferId,
          values.supportOfferId,
          ...optionsOfferIds,
        ].filter(Boolean) as string[],
        ...values,
        duration: values.duration || 30,
      } as any;
    },
    [organizationId],
  );

  const { onSubmit, loginIsOpened, closeLogin, onLoginSuccess } =
    useCreateStudy<QuoteInformation>({ createDto });

  const initialValuesWithProps = useMemo((): QuoteInformation => {
    return {
      ...initialValues,
      source,
    };
  }, [source]);

  return (
    <Formik
      initialValues={initialValuesWithProps}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ values }) => (
        <>
          <Form>
            <GeneralSetup askForSource={!source} {...rest} />
          </Form>
          <LoginModal
            isOpen={loginIsOpened}
            onRequestClose={closeLogin}
            onSuccess={onLoginSuccess}
            trackContext="session"
            billingMethod={values.billingMethod}
          />
        </>
      )}
    </Formik>
  );
};
